import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, Plug } from "lucide-react";
import { theme, card, emptyFrame, inputStyle, btnPrimary } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { ConnectorTrendPanel } from "../components/ConnectorTrendPanel";

interface AwsSummary {
  total:    number;
  critical: number;
  high:     number;
  medium:   number;
  low:      number;
  active:   number;
  resolved: number;
}

interface AwsStatus {
  connected:   boolean;
  region:      string | null;
  accessKeyId: string | null;
  lastSync:    string | null;
  lastError:   string | null;
  /** The estate read through the Resource Groups Tagging API for the asset register (needs tag:GetResources). */
  inventory?:  { resources: number; lastSync: string | null; lastError: string | null };
  summary:     AwsSummary;
}

interface AwsFinding {
  id:           string;
  findingId:    string;
  title:        string;
  description:  string | null;
  severity:     string;
  status:       string;
  resourceType: string | null;
  resourceId:   string | null;
  awsAccountId: string | null;
  region:       string | null;
  createdAt:    string;
  syncedAt:     string;
}

const AWS_REGIONS = [
  "us-east-1", "us-east-2", "us-west-1", "us-west-2",
  "eu-west-1", "eu-west-2", "eu-west-3", "eu-central-1", "eu-north-1",
  "ap-southeast-1", "ap-southeast-2", "ap-northeast-1", "ap-northeast-2",
  "ap-south-1", "ca-central-1", "sa-east-1",
];

const SEVERITY_COLORS: Record<string, { bg: string; text: string }> = {
  CRITICAL:      { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  HIGH:          { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  MEDIUM:        { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  LOW:           { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  INFORMATIONAL: { bg: "var(--c-faint)",     text: "var(--c-muted)" },
};

function Pill({ label, colors }: { label: string; colors: { bg: string; text: string } }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 9px", borderRadius: 3,
      fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

export function ConnectAwsPage() {
  const [status,       setStatus]       = useState<AwsStatus | null>(null);
  const [findings,     setFindings]     = useState<AwsFinding[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [syncing,      setSyncing]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [showForm,     setShowForm]     = useState(false);
  const [filterSeverity, setFilterSeverity] = useState("all");

  const [form, setForm] = useState({
    accessKeyId: "", secretAccessKey: "", region: "eu-west-1",
  });

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.get<AwsStatus>("/aws/status");
      setStatus(data);
      if (data.connected) {
        const params = new URLSearchParams();
        if (filterSeverity !== "all") params.set("severity", filterSeverity);
        const { findings: fs } = await api.get<{ findings: AwsFinding[] }>(`/aws/findings?${params}`);
        setFindings(fs);
      }
    } catch {
      setError("Failed to load AWS Security Hub status.");
    } finally {
      setLoading(false);
    }
  }, [filterSeverity]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post("/aws/connect", form);
      setShowForm(false);
      setForm({ accessKeyId: "", secretAccessKey: "", region: "eu-west-1" });
      await loadStatus();
      pollAfterSync();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setSaving(false);
    }
  }

  // Sync runs on the worker; poll status for a short while so the findings
  // list refreshes without the user reloading.
  async function pollAfterSync() {
    for (let i = 0; i < 6; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      await loadStatus();
    }
  }

  async function handleSync() {
    setSyncing(true);
    setError(null);
    try {
      await api.post("/aws/sync", {});
      await loadStatus();
      pollAfterSync();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Remove AWS Security Hub integration and delete all synced finding data?")) return;
    setDisconnecting(true);
    try {
      await api.del("/aws/connect");
      setFindings([]);
      await loadStatus();
    } catch {
      setError("Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) return <LoadingBlock label="Loading AWS Security Hub status…" />;
  if (!status) return <div style={{ color: "var(--c-dangerText)", padding: "20px" }}>{error || "Failed to load status"}</div>;
  const s = status;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <ShieldAlert size={22} color="var(--c-accentText)" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>
              AWS Security Hub
            </h2>
            {s.connected && (
              <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 3 }}>
                Connected
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Ingest active security findings from AWS Security Hub as evidence for AI compliance checks and risk assessments.
          </p>
        </div>
        {s.connected && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button onClick={handleSync} disabled={syncing}
              style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13, opacity: syncing ? 0.6 : 1, cursor: syncing ? "not-allowed" : "pointer" }}>
              {syncing ? "Syncing…" : "Sync Now"}
            </button>
            <button onClick={handleDisconnect} disabled={disconnecting}
              style={{ padding: "7px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: "var(--c-dangerText)", cursor: "pointer", opacity: disconnecting ? 0.6 : 1 }}>
              Disconnect
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Not connected */}
      {!s.connected && !showForm && (
        <div style={{ ...card, textAlign: "center", padding: "40px 28px" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Plug size={36} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            Connect AWS Security Hub
          </div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 560, margin: "0 auto 20px", lineHeight: 1.6 }}>
            Create an IAM user (or role) with the <strong>AWSSecurityHubReadOnlyAccess</strong> policy,
            generate an access key, then enter the details below. Findings from all enabled
            Security Hub standards (CIS, PCI DSS, AWS Foundational) sync on demand and feed
            directly into your AI compliance checks. Add <strong>tag:GetResources</strong> (the
            <strong>ResourceGroupsandTagEditorReadOnlyAccess</strong> policy) and the same sync also
            reads every resource in the region into your asset register.
          </p>
          <button onClick={() => setShowForm(true)} style={{ ...btnPrimary, padding: "9px 22px", fontSize: 13, cursor: "pointer" }}>
            Enter Details
          </button>
        </div>
      )}

      {/* Connect form */}
      {!s.connected && showForm && (
        <div style={card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
            AWS Credentials
          </h3>
          <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 18px", lineHeight: 1.6 }}>
            In IAM, create a user with <strong>AWSSecurityHubReadOnlyAccess</strong> attached
            (plus <strong>ResourceGroupsandTagEditorReadOnlyAccess</strong> for the asset inventory),
            then go to <strong>Security credentials → Create access key</strong>.
            Choose the region where Security Hub is enabled in your account.
          </p>
          <form onSubmit={handleConnect} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Access Key ID</span>
              <input
                type="text"
                value={form.accessKeyId}
                onChange={(e) => setForm((f) => ({ ...f, accessKeyId: e.target.value }))}
                placeholder="AKIAIOSFODNN7EXAMPLE"
                required
                style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Secret Access Key</span>
              <input
                type="password"
                value={form.secretAccessKey}
                onChange={(e) => setForm((f) => ({ ...f, secretAccessKey: e.target.value }))}
                placeholder="Your secret access key"
                required
                style={{ ...inputStyle, fontSize: 13 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, gridColumn: "1 / -1" }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>AWS Region</span>
              <select
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                style={{ ...inputStyle, fontSize: 13 }}
              >
                {AWS_REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </label>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, marginTop: 4 }}>
              <button type="submit" disabled={saving}
                style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Validating & Connecting…" : "Connect"}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connected */}
      {s.connected && (
        <>
          {/* Summary tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Total Findings", value: s.summary.total,    bg: theme.color.panel,    text: theme.color.heading },
              { label: "Critical",       value: s.summary.critical,  bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
              { label: "High",           value: s.summary.high,      bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
              { label: "Medium",         value: s.summary.medium,    bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
              { label: "Low",            value: s.summary.low,       bg: "var(--c-faint)",     text: "var(--c-muted)" },
              { label: "Active",         value: s.summary.active,    bg: "var(--c-accentBg)",  text: "var(--c-accentText)" },
            ].map((stat) => (
              <div key={stat.label} style={{ ...card, background: stat.bg, padding: "14px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: stat.text }}>{stat.value}</div>
                <div style={{ fontSize: 12, color: stat.text, marginTop: 2, opacity: 0.8 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Continuous monitoring: the daily readings over 90 days */}
          <ConnectorTrendPanel connector="aws" />

          {/* Meta */}
          <div style={{ ...card, padding: "12px 16px", marginBottom: 20, display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12, color: theme.color.muted }}>
            <span>Region: <strong style={{ color: theme.color.text }}>{s.region}</strong></span>
            <span>Key ID: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{s.accessKeyId}</strong></span>
            <span>Last sync: <strong style={{ color: theme.color.text }}>{fmtDate(s.lastSync)}</strong></span>
            {s.inventory && <span title="Resources in the connected region read through the Resource Groups Tagging API — the asset register's AWS inventory">Inventory: <strong style={{ color: theme.color.text }}>{s.inventory.lastSync ? `${s.inventory.resources} resources, ${fmtDate(s.inventory.lastSync)}` : "not read yet"}</strong></span>}
          </div>
          {s.lastError && (
            <div style={{ ...card, padding: "10px 16px", marginBottom: 20, fontSize: 12, color: theme.color.dangerText, background: theme.color.dangerBg, borderColor: theme.color.danger }}>
              Last sync failed: {s.lastError}
            </div>
          )}
          {s.inventory?.lastError && (
            <div style={{ ...card, padding: "12px 16px", marginBottom: 20, fontSize: 12, color: "var(--c-warningText)", background: "var(--c-warningBg)" }}>
              Asset inventory not read — findings still sync. {s.inventory.lastError}
            </div>
          )}

          {/* Severity filter */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {["all", "CRITICAL", "HIGH", "MEDIUM", "LOW", "INFORMATIONAL"].map((f) => {
              const active = filterSeverity === f;
              return (
                <button key={f} onClick={() => setFilterSeverity(f)} style={{
                  padding: "5px 13px", borderRadius: 3, fontSize: 12,
                  fontWeight: active ? 600 : 400,
                  border: active ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                  background: active ? "var(--c-accentBg)" : "var(--c-panel)",
                  color: active ? "var(--c-accentText)" : theme.color.muted, cursor: "pointer",
                }}>
                  {f === "all" ? "All Severity" : f}
                </button>
              );
            })}
          </div>

          {/* Findings table */}
          {findings.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.muted, fontSize: 13 }}>
              No findings match this filter.
            </div>
          ) : (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
                      {["Title", "Severity", "Resource", "Account", "Created"].map((h) => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((f, i) => (
                      <tr key={f.id} style={{ borderBottom: i < findings.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                        <td style={{ padding: "10px 14px", maxWidth: 320 }}>
                          <div style={{ fontWeight: 500, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.title}</div>
                          {f.description && (
                            <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.description}</div>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          <Pill label={f.severity} colors={SEVERITY_COLORS[f.severity] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                        </td>
                        <td style={{ padding: "10px 14px", color: theme.color.muted, fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.resourceType ? (
                            <span title={f.resourceId ?? undefined}>{f.resourceType.replace("AWS::", "")}</span>
                          ) : "—"}
                        </td>
                        <td style={{ padding: "10px 14px", color: theme.color.faint, fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>
                          {f.awsAccountId ?? "—"}
                        </td>
                        <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap", fontSize: 12 }}>
                          {fmtDate(f.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
